import type { User } from '../auth/types';
import { replaceDocumentLocation } from './documentNavigation';
import { readAuthorizedReturnTo } from './returnTo';
import type { RouterBasename } from './types';

const POST_LOGIN_TARGET_BASENAMES: readonly RouterBasename[] = [
  '/',
  '/app',
  '/manager',
  '/hr',
  '/accountant',
];

function readSingleAuthorizedPostLoginReturn(search: string, user: User): string | null {
  const matches = POST_LOGIN_TARGET_BASENAMES
    .map((basename) => readAuthorizedReturnTo(search, { user, basename }))
    .filter((target): target is string => target !== null);
  return matches.length === 1 ? matches[0] : null;
}

function isAuthorizedFallback(target: string, user: User): boolean {
  const search = `?returnTo=${encodeURIComponent(target)}`;
  return POST_LOGIN_TARGET_BASENAMES.some((basename) => (
    readAuthorizedReturnTo(search, { user, basename }) === target
  ));
}

function roleLandingCandidates(user: User): string[] {
  const role = String(user.role ?? '').trim().toLowerCase();
  const candidates: string[] = [];

  if (role === 'student') candidates.push('/student/dashboard');
  if (role === 'developer' || user.is_developer === true) candidates.push('/developer/cockpit');
  if (role === 'general_manager') candidates.push('/manager');
  if (role.includes('hr')) candidates.push('/hr');
  if (role === 'accountant') candidates.push('/accountant');
  if (role === 'moderator') candidates.push('/marketing/moderator');
  if (role === 'marketing') candidates.push('/marketing/dashboard');
  if (role.includes('instructor')) candidates.push('/instructor');
  if (role === 'tele_sales') candidates.push('/sales/tele-sales');
  if (['sales', 'agent', 'sales_manager', 'ghosted_sales'].includes(role)) candidates.push('/sales');
  if (role.includes('reception') || (role.includes('manager') && role !== 'sales_manager')) {
    candidates.push('/reception');
  }

  candidates.push('/employee/profile');
  return candidates;
}

function resolveAuthorizedRoleLanding(user: User): string {
  return roleLandingCandidates(user).find((target) => isAuthorizedFallback(target, user))
    ?? '/employee/profile';
}

export function resolveStaffPostLoginTarget(search: string, user: User): string {
  return readSingleAuthorizedPostLoginReturn(search, user) ?? resolveAuthorizedRoleLanding(user);
}

export function resolveStudentPostLoginTarget(search: string, user: User): string {
  return readSingleAuthorizedPostLoginReturn(search, user) ?? resolveAuthorizedRoleLanding(user);
}

export function navigateToDocument(target: string): void {
  replaceDocumentLocation(target);
}
